export interface SubscriptionLifecycle<TSubscription> {
  subscribe: (options: PushSubscriptionOptionsInit) => Promise<TSubscription>;
  getSubscription: () => Promise<TSubscription | null>;
  unsubscribe: (subscription: TSubscription) => Promise<void>;
}

// Known recoverable DOMException types for Web Push API
const RECOVERABLE_ERROR_NAMES = new Set([
  "InvalidStateError",
  // Future: Add other recoverable error types as needed
  // "NotAllowedError", // Example: if user denies permission
  // "AbortError",      // Example: if subscription is aborted
]);

function isRecoverableError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const maybeNamed = error as { name?: unknown };
  return (
    typeof maybeNamed.name === "string" &&
    RECOVERABLE_ERROR_NAMES.has(maybeNamed.name)
  );
}

export async function subscribeWithRecovery<TSubscription>(
  lifecycle: SubscriptionLifecycle<TSubscription>,
  options: PushSubscriptionOptionsInit
): Promise<TSubscription> {
  try {
    return await lifecycle.subscribe(options);
  } catch (error) {
    if (!isRecoverableError(error)) {
      throw error;
    }

    const existing = await lifecycle.getSubscription();
    if (!existing) {
      throw error;
    }

    try {
      await lifecycle.unsubscribe(existing);
    } catch {
      // Ignore unsubscribe errors and attempt resubscription regardless
    }

    return lifecycle.subscribe(options);
  }
}
